import { request } from '@/fetch/core';
import { CoordType } from '@/utils/enum';

/** 路径规划经纬度（GCJ02） */
export interface RoutePlanLngLat {
  lng: number;
  lat: number;
}

/** 路径规划接口入参端点（GCJ02） */
export interface WebRoutePlanEndpoint {
  latitude: number;
  longitude: number;
}

/** 路径规划接口入参 */
export interface GetWebRoutePlanParams {
  origin: WebRoutePlanEndpoint;
  destination: WebRoutePlanEndpoint;
  /** 是否返回备选方案，固定 0 */
  alternatives: 0;
}

/** 路径规划接口返回的路径点 */
export interface WebRoutePlanPoint {
  longitude: number;
  latitude: number;
}

/** 路径规划接口返回数据 */
export interface WebRoutePlanData {
  /** 里程，单位：米 */
  distance: number;
  /** 耗时，单位：秒 */
  duration: number;
  path: WebRoutePlanPoint[];
}

/** 路径规划接口响应（与 fetch/core request 的实际返回形态对齐） */
export interface RoutePlanResponse {
  code: string;
  data?: WebRoutePlanData;
  message?: string;
}

class MapEditorFetch {
  getEnum = () => {
    const requestOptions: RequestOptions = {
      path: '/map/web/enum/get_enum_list_map',
      method: 'POST',
      body: {
        keyList: [
          'VARIABLE_MAP_DURATION_TYPE_ENUM',
          'VARIABLE_MAP_EFFECT_DATE_TYPE_ENUM',
          'VARIABLE_MAP_STATUS_ENUM',
          'VARIABLE_MAP_TYPE_ENUM',
          'ATTRIBUTE_ANNOTATION_TYPE_ENUM',
          'LD_ASSIST_TYPE_ENUM',
          'NAVIGATION_TOPOLOGY',
          'NAVIGATION_DIRECTION',
        ],
      },
    };
    return request(requestOptions);
  };

  /**
   * 新增动态地图
   * @param params
   * @returns
   */
  addDynamicMap = (params: any) => {
    const requestOptions: RequestOptions = {
      path: '/map/web/mapVariable/add',
      method: 'POST',
      body: params,
    };
    return request(requestOptions);
  };
  /**
   * 删除动态地图
   * @param id 地图id
   */
  delDynamicMap = (id: any) => {
    const requestOptions: RequestOptions = {
      path: '/map/web/mapVariable/deleteById',
      method: 'POST',
      body: id,
    };
    return request(requestOptions);
  };
  /**
   * 编辑动态地图
   * @param params
   */
  editDynamicMap = (params: any) => {
    const requestOptions: RequestOptions = {
      path: '/map/web/mapVariable/update',
      method: 'POST',
      body: params,
    };
    return request(requestOptions);
  };
  /**
   * 查询动态地图列表
   * @param body
   * @returns
   */
  dofetchRecordList = (body: any) => {
    const options = {
      method: 'POST',
      path: 'map/web/mapVariable/list',
      body: {
        ...body,
        coordType: 'GCJ02',
      },
    };
    return request(options);
  };
  /**
   * 用户权限下停靠点列表
   * @param params
   * @returns
   */
  getStopList = (params: any) => {
    const options = {
      method: 'POST',
      path: '/monitor/web/basic/user_metadata_list',
      body: {
        ...params,
        dataType: 'stop',
      },
    };
    return request(options);
  };

  /**
   * 请求城市、站点、及站点下的车辆列表
   * @return {Promise}
   */
  fetchCityAndStationList = () => {
    const requestParam: RequestOptions = {
      method: 'POST',
      path: '/monitor/web/basic/user_metadata',
      body: { dataType: 'city' },
    };
    return request(requestParam);
  };
  /**
   * 用户权限下停靠点列表
   * @param name  模糊搜索name
   * @returns
   */
  fetchStopList = (name: string) => {
    const requestParam: RequestOptions = {
      method: 'POST',
      path: '/monitor/web/basic/user_metadata_list',
      body: {
        name,
        dataType: 'stop',
      },
    };
    return request(requestParam);
  };

  // 获取车辆列表
  fetchVehicleList = () => {
    const requestParam: RequestOptions = {
      method: 'POST',
      path: '/monitor/web/basic/user_metadata',
      body: { dataType: 'vehicle' },
    };
    return request(requestParam);
  };
  /**
   * 根据基础数据类型获取实时经纬度
   * @returns
   */
  fetchPostionInfo = ({
    metaType,
    key,
    positionType,
  }: {
    metaType: string | null;
    key: string | number;
    positionType: CoordType;
  }) => {
    const requestParam: RequestOptions = {
      method: 'POST',
      path: '/monitor/web/map/position',
      body: { metaType, key, positionType },
    };
    return request(requestParam);
  };
  deleteMapVariable = (id: number) => {
    const requestParam: RequestOptions = {
      method: 'POST',
      path: '/map/web/mapVariable/deleteById',
      body: id,
    };
    return request(requestParam);
  };
  getMapByPosition = (params: any) => {
    const requestParam: RequestOptions = {
      method: 'POST',
      path: '/map/web/mapInfo/getMapByPosition',
      body: {
        longitude: params.longitude,
        latitude: params.latitude,
      },
    };
    return request(requestParam);
  };
  getSizeOfVehicle() {
    const requestOptions: RequestOptions = {
      path: '/k2/management/device_type/get_size_of_device',
      method: 'POST',
    };
    return request(requestOptions);
  }

  getLaneCoordinates({
    mapId,
    mapVersion,
    geometry,
  }: {
    mapId: string | number;
    mapVersion: string | number;
    geometry: any;
  }) {
    const requestOptions: RequestOptions = {
      path: '/map/web/mapLane/getLaneListByPolygon',
      method: 'POST',
      body: {
        mapId,
        mapVersion,
        geometry,
        coordType: 'GCJ02',
      },
    };
    return request(requestOptions);
  }

  getLaneCoordinatesById({
    mapId,
    mapVersion,
    laneIdList,
  }: {
    mapId: string | number;
    mapVersion: string | number;
    laneIdList: any[];
  }) {
    const requestOptions: RequestOptions = {
      path: '/map/web/mapLane/getByLaneIdList',
      method: 'POST',
      body: {
        mapId,
        mapVersion,
        laneIdList,
        coordType: 'GCJ02',
      },
    };
    return request(requestOptions);
  }

  getSpeedLimitInfo(id: any) {
    const requestOptions: RequestOptions = {
      path: '/map/web/mapVariable/getById',
      method: 'POST',
      body: id,
    };
    return request(requestOptions);
  }

  editEnable(params: { id: any; status: string }) {
    const requestOptions: RequestOptions = {
      path: '/map/web/mapVariable/enable',
      method: 'POST',
      body: params,
    };
    return request(requestOptions);
  }

  /**
   * 获取路测关注区测试项列表
   * @returns 测试项列表
   */
  getRoadTestItemList = () => {
    const requestOptions: RequestOptions = {
      path: '/monitor/web/roadtest/getItemList',
      method: 'POST',
    };
    return request(requestOptions);
  };

  /**
   * 路径规划查看（GCJ02 起终点 → 最佳路径）
   * @param body { start, end }
   */
  getWebRoutePlan = (body: GetWebRoutePlanParams): Promise<RoutePlanResponse> => {
    const requestOptions: RequestOptions = {
      path: '/map/web/ld/route/getWebRoutePlan',
      method: 'POST',
      body,
    };
    return request(requestOptions);
  };
}

export default MapEditorFetch;

/** MapEditorFetch 单例：整个应用共享一个请求实例，避免组件内重复 new */
export const mapEditorFetch = new MapEditorFetch();
